import { useEffect, useState } from "react";
import { mntAPI, fmtINR } from "./mntApi";
import { toast } from "sonner";
import { Plus, X, Save, Boxes } from "lucide-react";
import { NumberInput } from "@/components/ui/number-input";

const CONDITION_STYLE = {
  new:    "bg-emerald-50 text-emerald-700",
  good:   "bg-sky-50 text-sky-700",
  fair:   "bg-amber-50 text-amber-700",
  poor:   "bg-orange-50 text-orange-700",
  faulty: "bg-rose-50 text-rose-700",
};

const STATUS_STYLE = {
  active:              "bg-emerald-50 text-emerald-700",
  under_maintenance:   "bg-amber-50 text-amber-700",
  out_of_service:      "bg-rose-50 text-rose-700",
  disposed:            "bg-slate-100 text-slate-500",
};

export default function MaintenanceAssets() {
  const [assets, setAssets] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [creating, setCreating] = useState(false);

  const load = () => mntAPI.assets({
    q: q || undefined,
    category_id: cat || undefined,
  }).then(setAssets).catch(() => {});
  useEffect(() => { load(); }, [q, cat]); // eslint-disable-line
  useEffect(() => { mntAPI.categories().then(setCats); }, []);

  return (
    <div className="space-y-4" data-testid="mnt-assets">
      <div className="flex flex-wrap items-center gap-3">
        <input value={q} onChange={e => setQ(e.target.value)}
               placeholder="Search name, code, serial…"
               data-testid="mnt-assets-search"
               className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px] min-w-[240px]"/>
        <select value={cat} onChange={e => setCat(e.target.value)}
                data-testid="mnt-assets-cat-filter"
                className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px]">
          <option value="">All categories</option>
          {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button onClick={() => setCreating(true)} data-testid="mnt-assets-new"
                className="ml-auto h-10 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center gap-2">
          <Plus className="w-4 h-4"/> Add Asset
        </button>
      </div>

      {!assets.length ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-10 text-center text-[13px] text-slate-500">
          <Boxes className="w-6 h-6 mx-auto text-slate-300 mb-2"/>
          No assets registered yet. Add your first asset.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {assets.map(a => {
            const cs = CONDITION_STYLE[a.condition] || CONDITION_STYLE.good;
            const ss = STATUS_STYLE[a.status] || STATUS_STYLE.active;
            const catName = cats.find(c => c.id === a.category_id)?.name;
            return (
              <div key={a.id} data-testid={`mnt-asset-${a.id}`}
                   className="bg-white border border-slate-200 rounded-2xl p-4 hover:shadow-md hover:border-slate-300 transition-all">
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10.5px] mono text-slate-400">{a.asset_code}</div>
                    <div className="font-display text-[15px] font-semibold text-hocp-navy leading-tight mt-0.5">{a.name}</div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-md text-[11px] font-medium ${ss}`}>{(a.status || "").replace("_", " ")}</span>
                </div>
                <div className="text-[12px] text-slate-500 space-y-1 mb-3">
                  {catName && <div><span className="text-slate-400">Category</span> · {catName}</div>}
                  {a.manufacturer && <div><span className="text-slate-400">Make</span> · {a.manufacturer} {a.model || ""}</div>}
                  {a.location?.room_no && <div><span className="text-slate-400">Location</span> · Room {a.location.room_no}</div>}
                  {a.serial_number && <div className="mono text-[11px]"><span className="text-slate-400">SN</span> · {a.serial_number}</div>}
                </div>
                <div className="flex items-center justify-between text-[11px] pt-3 border-t border-slate-100">
                  <span className={`px-2 py-0.5 rounded-md font-medium ${cs}`}>{a.condition}</span>
                  {a.replacement_cost && <span className="text-slate-500 tabular-nums">{fmtINR(a.replacement_cost)}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {creating && <AssetDialog cats={cats}
                                 onClose={() => setCreating(false)}
                                 onCreated={() => { setCreating(false); load(); }}/>}
    </div>
  );
}

function AssetDialog({ cats, onClose, onCreated }) {
  const [f, setF] = useState({
    asset_code: "", name: "", category_id: cats[0]?.id || "",
    manufacturer: "", model: "", serial_number: "",
    condition: "good", replacement_cost: 0, expected_life_months: 60,
    location: { room_no: "", floor: "", area: "" },
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!f.asset_code || !f.name || !f.category_id) {
      toast.error("Asset code, name, and category are required"); return;
    }
    setSaving(true);
    try {
      await mntAPI.createAsset({
        ...f,
        replacement_cost: Number(f.replacement_cost),
        expected_life_months: Number(f.expected_life_months),
        location: Object.values(f.location).some(Boolean) ? f.location : undefined,
      });
      toast.success("Asset created"); onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-[16px] font-semibold text-hocp-navy">New Asset</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X className="w-4 h-4 text-slate-500"/></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <F label="Asset Code"><input value={f.asset_code} onChange={e => setF({...f, asset_code: e.target.value.toUpperCase()})} data-testid="new-asset-code" className={cls}/></F>
          <F label="Name"><input value={f.name} onChange={e => setF({...f, name: e.target.value})} data-testid="new-asset-name" className={cls}/></F>
          <F label="Category"><select value={f.category_id} onChange={e => setF({...f, category_id: e.target.value})} data-testid="new-asset-cat" className={cls}>
            {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select></F>
          <F label="Condition"><select value={f.condition} onChange={e => setF({...f, condition: e.target.value})} className={cls}>
            <option value="new">New</option><option value="good">Good</option>
            <option value="fair">Fair</option><option value="poor">Poor</option>
            <option value="faulty">Faulty</option>
          </select></F>
          <F label="Manufacturer"><input value={f.manufacturer} onChange={e => setF({...f, manufacturer: e.target.value})} className={cls}/></F>
          <F label="Model"><input value={f.model} onChange={e => setF({...f, model: e.target.value})} className={cls}/></F>
          <F label="Serial Number"><input value={f.serial_number} onChange={e => setF({...f, serial_number: e.target.value})} className={cls + " mono"}/></F>
          <F label="Replacement Cost (₹)"><NumberInput value={f.replacement_cost} onChange={e => setF({...f, replacement_cost: e.target.value})} className={cls}/></F>
          <F label="Room No."><input value={f.location.room_no} onChange={e => setF({...f, location: {...f.location, room_no: e.target.value}})} className={cls}/></F>
          <F label="Floor"><input value={f.location.floor} onChange={e => setF({...f, location: {...f.location, floor: e.target.value}})} className={cls}/></F>
          <F label="Area / Zone" full><input value={f.location.area} onChange={e => setF({...f, location: {...f.location, area: e.target.value}})} placeholder="e.g. Pool deck, Kitchen west" className={cls}/></F>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button onClick={onClose} className="h-9 px-3.5 rounded-lg text-slate-600 text-[13px] hover:bg-slate-100">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="new-asset-submit"
                  className="h-9 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center gap-2">
            <Save className="w-4 h-4"/> {saving ? "Saving…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

const cls = "w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px] outline-none focus:border-hocp-navy transition";
function F({ label, children, full }) {
  return <label className={`block ${full ? "col-span-2" : ""}`}><div className="text-[11.5px] text-slate-500 mb-1 font-medium">{label}</div>{children}</label>;
}
